import React, { useState, useEffect } from 'react';
import appBranding from '@happylife-a/branding';
import webCore from '@happylife-a/web-core';
import webApp from '@happylife-a/web-app';
import {
  Flex,
  Text,
  Icon,
  Box,
  Stack,
  Button,
  Divider,
  usePrefersReducedMotion,
  Image,
  Spinner,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useLocation } from 'react-router-dom';
import { theme } from '../../theme';
import ScrollableBox from '../ScrollableBox';
import NavItem from './NavItem';
import { useAppLocale } from '../../contexts';

const spin = keyframes`
  0%    { opacity: 0; }
  15%   { opacity: 0.8; }
  55%   { opacity: 0.9; }
  100%  { opacity: 1; }
`;

export default function Sidebar({ onLogout, navSize, sidebarItems, isDisabled }) {
  const location = useLocation();
  const { t, locale } = webCore.contexts.useTranslation();
  const { initialPrefix } = useAppLocale();
  const prefersReducedMotion = usePrefersReducedMotion();
  const animation = prefersReducedMotion
    ? undefined
    : `${spin} infinite 5s linear; animation-iteration-count: 1`;

  const longTextProps = {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    width: '100%',
    whiteSpace: 'nowrap',
    animation: animation,
  };

  const currentPath = webApp.helpers.locale.stripLocale(location.pathname);

  // State for logo processing
  const [isLogoProcessing, setIsLogoProcessing] = useState(false);

  // Check if logo is being processed on mount
  useEffect(() => {
    const checkLogoProcessing = async () => {
      const uploadTimestamp = await webCore.helpers.storage.getItem('logo_upload_timestamp');

      if (uploadTimestamp) {
        const uploadTime = parseInt(uploadTimestamp, 10);
        const currentTime = Date.now();
        const oneMinuteInMs = 1 * 60 * 1000;

        if (currentTime - uploadTime < oneMinuteInMs) {
          setIsLogoProcessing(true);

          // Set timeout to stop showing processing after remaining time
          const remainingTime = oneMinuteInMs - (currentTime - uploadTime);
          setTimeout(() => {
            setIsLogoProcessing(false);
          }, remainingTime);
        }
      }
    };

    checkLogoProcessing();

    // Listen for storage changes (cross-tab communication)
    const handleStorageChange = (e) => {
      if (e.key === 'logo_upload_timestamp' && e.newValue) {
        checkLogoProcessing();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <Flex
      pos="relative"
      h="100vh"
      bg="white"
      w="full"
      flexDir="column"
      justifyContent="space-between"
    >
      <Flex flexDir="column" w="100%" as="nav" pt={5}>
        <Stack direction="row" justifyContent="space-between" px={6}>
          <Flex alignItems="center" justifyContent="center" gap={5}>
            <Box position="relative" width="40px" height="40px">
              <Image
                src={appBranding.configs.assets.getAsset('logos/svg/square-colored.svg')}
                objectFit="contain"
                width="40px"
                height="40px"
              />
              {isLogoProcessing && (
                <Flex
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  bg="rgba(255, 255, 255, 0.9)"
                  borderRadius="8px"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gap={1}
                >
                  <Spinner size="sm" color="primary.500" thickness="2px" />
                </Flex>
              )}
            </Box>
          </Flex>
        </Stack>

        <ScrollableBox
          primary={false}
          mt={5}
          position="relative"
          overflowY={'auto'}
          overflowX={'hidden'}
          w="100%"
          maxH="calc(100vh - 183px)"
        >
          {sidebarItems.map((item, idx) => {
            const currentPathWithoutPrefix = currentPath
              .replace(initialPrefix ? `/${initialPrefix}` : '', '')
              .replace(`/${locale}`, '');
            const isActive =
              currentPathWithoutPrefix === item.to ||
              currentPathWithoutPrefix.startsWith(item.to + '/');

            return (
              <Box key={`navItem-${item.name}-${idx}`} px={navSize === 'large' ? 7 : 0}>
                <NavItem
                  isDisabled={isDisabled}
                  navSize={navSize}
                  icon={item.icon}
                  title={item.name}
                  to={item.to}
                  query={item.query}
                  isDashboard={idx === 0 && true}
                  active={isActive}
                  textProps={longTextProps}
                />
              </Box>
            );
          })}
        </ScrollableBox>
      </Flex>

      <Box px={7} pb={7}>
        <Divider />

        <Flex flexDir="column" w="100%" alignItems={navSize === 'small' ? 'center' : 'flex-start'}>
          <Flex align="center">
            <Flex flexDir="column">
              {/* @TODO: uncomment once support will be ready */}
              {/* <Flex
                alignItems="center"
                justifyContent={navSize === 'small' ? 'center' : 'inherit'}
                px={4}
                py={3}
                bg="grey.50"
                borderRadius="16px"
              >
                <Icon
                  as={theme.icons.SidebarHeadphones}
                  fill="none"
                  w={6}
                  h={6}
                />
                <Text
                  ml={4}
                  variant="body-reg-lg"
                  textAlign="left"
                  display={navSize === 'small' ? 'none' : 'flex'}
                  {...longTextProps}
                >
                  {t('Contact support')}
                </Text>
              </Flex> */}
              <Flex alignItems="center" px={4} py={3} mt={4}>
                <Button variant="link" onClick={onLogout}>
                  <Icon as={theme.icons.SidebarLogOut} fill="none" w={6} h={6} />
                  <Text
                    ml={4}
                    variant="body-reg-lg"
                    textAlign="left"
                    color="red.500"
                    display={navSize === 'small' ? 'none' : 'flex'}
                    {...longTextProps}
                  >
                    {t('Logout')}
                  </Text>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
}
